import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Share2,
  Plus,
  AlertCircle,
  CheckCircle2,
  Clock,
  Calendar,
  List
} from 'lucide-react';
import { useSocialAccounts } from '@/hooks/social/useSocialAccounts';
import { useSocialPosts } from '@/hooks/social/useSocialPosts';
import { useAutoReplyRules } from '@/hooks/social/useAutoReplyRules';
import { ConnectedAccounts } from './accounts/ConnectedAccounts';
import { ConnectAccountDialog } from './accounts/ConnectAccountDialog';
import { PostComposer } from './posts/PostComposer';
import { PostsList } from './posts/PostsList';
import { PostCalendar } from './posts/PostCalendar';
import { UnifiedInbox } from './inbox/UnifiedInbox';
import { SocialAnalyticsDashboard } from './analytics/SocialAnalyticsDashboard';
import { AutoReplyRulesList } from './auto-reply/AutoReplyRulesList';
import { AutoReplyRuleBuilder } from './auto-reply/AutoReplyRuleBuilder';
import { SocialAccount, SocialPost, SocialAutoReplyRule } from '@/types/social';

interface SocialMediaTabProps {
  businessId: string | null;
  activeView?: string;
}

export const SocialMediaTab: React.FC<SocialMediaTabProps> = ({
  businessId,
  activeView = 'social-accounts'
}) => {
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<SocialPost | null>(null);
  const [postsViewMode, setPostsViewMode] = useState<'list' | 'calendar'>('list');
  const [scheduleDateFromCalendar, setScheduleDateFromCalendar] = useState<Date | null>(null);
  const [isRuleBuilderOpen, setIsRuleBuilderOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<SocialAutoReplyRule | null>(null);

  const { accounts, isLoading: accountsLoading, error: accountsError, refresh: refreshAccounts } = useSocialAccounts(businessId);
  const {
    posts,
    isLoading: postsLoading,
    error: postsError,
    createPost,
    updatePost,
    deletePost,
    publishNow,
    refresh: refreshPosts
  } = useSocialPosts(businessId);
  const {
    rules,
    isLoading: rulesLoading,
    createRule,
    updateRule,
    deleteRule,
    toggleRule,
    refresh: refreshRules
  } = useAutoReplyRules(businessId);

  const hasConnectedAccounts = accounts.length > 0;
  const activeAccounts = accounts.filter(a => a.status === 'active');

  // Handle creating a new post (optionally with a pre-selected date from calendar)
  const handleCreatePost = (date?: Date) => {
    setEditingPost(null);
    setScheduleDateFromCalendar(date || null);
    setIsComposerOpen(true);
  };

  // Handle editing an existing post
  const handleEditPost = (post: SocialPost) => {
    setEditingPost(post);
    setScheduleDateFromCalendar(null);
    setIsComposerOpen(true);
  };

  // Handle composer close
  const handleComposerClose = () => {
    setIsComposerOpen(false);
    setEditingPost(null);
    setScheduleDateFromCalendar(null);
  };

  // Render content based on activeView
  const renderContent = () => {
    switch (activeView) {
      case 'social-accounts':
        return (
          <ConnectedAccounts
            businessId={businessId}
            accounts={accounts}
            isLoading={accountsLoading}
            onConnectNew={() => setIsConnectDialogOpen(true)}
            onRefresh={refreshAccounts}
          />
        );

      case 'social-posts':
        return (
          <PostSchedulingContent
            businessId={businessId!}
            accounts={activeAccounts}
            posts={posts}
            isLoading={postsLoading}
            viewMode={postsViewMode}
            onViewModeChange={setPostsViewMode}
            onCreatePost={handleCreatePost}
            onEditPost={handleEditPost}
            onDeletePost={deletePost}
            onPublishNow={publishNow}
          />
        );

      case 'social-inbox':
        return (
          <UnifiedInbox
            businessId={businessId!}
            accounts={activeAccounts}
          />
        );

      case 'social-analytics':
        return (
          <SocialAnalyticsDashboard
            businessId={businessId!}
            accounts={activeAccounts}
          />
        );

      case 'social-auto-reply':
        if (isRuleBuilderOpen) {
          return (
            <AutoReplyRuleBuilder
              businessId={businessId!}
              editingRule={editingRule}
              onSave={async (request) => {
                if (editingRule) {
                  await updateRule(editingRule.id, request);
                } else {
                  await createRule(request);
                }
                setIsRuleBuilderOpen(false);
                setEditingRule(null);
                refreshRules();
              }}
              onCancel={() => {
                setIsRuleBuilderOpen(false);
                setEditingRule(null);
              }}
            />
          );
        }
        return (
          <AutoReplyRulesList
            rules={rules}
            isLoading={rulesLoading}
            onCreateRule={() => {
              setEditingRule(null);
              setIsRuleBuilderOpen(true);
            }}
            onEditRule={(rule) => {
              setEditingRule(rule);
              setIsRuleBuilderOpen(true);
            }}
            onDeleteRule={deleteRule}
            onToggleRule={toggleRule}
            onDuplicateRule={async (rule) => {
              await createRule({
                business_id: businessId!,
                name: `${rule.name} (Copy)`,
                trigger_type: rule.trigger_type,
                trigger_config: rule.trigger_config,
                platforms: rule.platforms,
                response_type: rule.response_type,
                response_content: rule.response_content,
                response_delay_seconds: rule.response_delay_seconds,
                max_triggers_per_contact_per_day: rule.max_triggers_per_contact_per_day,
                active_schedule: rule.active_schedule,
              });
              refreshRules();
            }}
          />
        );

      default:
        return null;
    }
  };

  // If no business selected
  if (!businessId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <Share2 className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
            <CardTitle>Select a Business</CardTitle>
            <CardDescription>
              Please select a business to manage its social media accounts.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick stats for connected accounts */}
      {hasConnectedAccounts && activeView === 'social-accounts' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickStatCard
            label="Connected"
            value={accounts.length}
            icon={Share2}
          />
          <QuickStatCard
            label="Active"
            value={activeAccounts.length}
            icon={CheckCircle2}
            variant="success"
          />
          <QuickStatCard
            label="Pending"
            value={accounts.filter(a => a.status === 'pending').length}
            icon={Clock}
            variant="warning"
          />
          <QuickStatCard
            label="Issues"
            value={accounts.filter(a => a.status === 'expired' || a.status === 'disconnected').length}
            icon={AlertCircle}
            variant="destructive"
          />
        </div>
      )}

      {/* Main content */}
      {renderContent()}

      {/* Connect Account Dialog */}
      <ConnectAccountDialog
        businessId={businessId}
        open={isConnectDialogOpen}
        onOpenChange={setIsConnectDialogOpen}
        onSuccess={() => {
          refreshAccounts();
          setIsConnectDialogOpen(false);
        }}
      />

      {/* Post Composer Dialog */}
      <Dialog open={isComposerOpen} onOpenChange={handleComposerClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? 'Edit Post' : 'Create New Post'}
            </DialogTitle>
          </DialogHeader>
          <PostComposer
            businessId={businessId!}
            accounts={activeAccounts}
            editingPost={editingPost}
            initialScheduleDate={scheduleDateFromCalendar}
            onSubmit={async (request) => {
              if (editingPost) {
                await updatePost(editingPost.id, request);
              } else {
                await createPost(request);
              }
              handleComposerClose();
              refreshPosts();
            }}
            onCancel={handleComposerClose}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Post Scheduling Content Component
interface PostSchedulingContentProps {
  businessId: string;
  accounts: SocialAccount[];
  posts: SocialPost[];
  isLoading: boolean;
  viewMode: 'list' | 'calendar';
  onViewModeChange: (mode: 'list' | 'calendar') => void;
  onCreatePost: (date?: Date) => void;
  onEditPost: (post: SocialPost) => void;
  onDeletePost: (postId: string) => Promise<void>;
  onPublishNow: (postId: string) => Promise<void>;
}

const PostSchedulingContent: React.FC<PostSchedulingContentProps> = ({
  businessId,
  accounts,
  posts,
  isLoading,
  viewMode,
  onViewModeChange,
  onCreatePost,
  onEditPost,
  onDeletePost,
  onPublishNow
}) => {
  const hasActiveAccounts = accounts.length > 0;

  // If no connected accounts, show a message
  if (!hasActiveAccounts) {
    return (
      <div className="space-y-6">
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Share2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Connect an Account First</h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-4">
              You need to connect at least one social media account before you can create and schedule posts.
            </p>
            <Button variant="outline" onClick={() => window.location.hash = '#social-accounts'}>
              <Plus className="h-4 w-4 mr-2" />
              Connect Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-end gap-2">
        {/* View Mode Toggle */}
        <div className="flex items-center border rounded-lg p-1">
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8"
            onClick={() => onViewModeChange('list')}
          >
            <List className="h-4 w-4 mr-1" />
            List
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8"
            onClick={() => onViewModeChange('calendar')}
          >
            <Calendar className="h-4 w-4 mr-1" />
            Calendar
          </Button>
        </div>
        <Button onClick={() => onCreatePost()}>
          <Plus className="h-4 w-4 mr-2" />
          Create Post
        </Button>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'calendar' ? (
        <PostCalendar
          posts={posts}
          onDayClick={(date) => onCreatePost(date)}
          onPostClick={onEditPost}
          onCreatePost={onCreatePost}
        />
      ) : (
        <PostsList
          posts={posts}
          isLoading={isLoading}
          onEdit={onEditPost}
          onDelete={onDeletePost}
          onPublishNow={onPublishNow}
        />
      )}
    </div>
  );
};

// Quick stat card component
interface QuickStatCardProps {
  label: string;
  value: number;
  icon: React.ElementType;
  variant?: 'default' | 'success' | 'warning' | 'destructive';
}

const QuickStatCard: React.FC<QuickStatCardProps> = ({
  label,
  value,
  icon: Icon,
  variant = 'default'
}) => {
  const variantStyles = {
    default: 'text-muted-foreground',
    success: 'text-green-600',
    warning: 'text-yellow-600',
    destructive: 'text-red-600'
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <Icon className={`h-8 w-8 ${variantStyles[variant]}`} />
        </div>
      </CardContent>
    </Card>
  );
};

// Coming soon placeholder component
interface ComingSoonCardProps {
  title: string;
  description: string;
  icon: React.ElementType;
  features: string[];
}

const ComingSoonCard: React.FC<ComingSoonCardProps> = ({
  title,
  description,
  icon: Icon,
  features
}) => {
  return (
    <div className="space-y-6">
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center text-center py-8">
            <div className="rounded-full bg-primary/10 p-4 mb-4">
              <Icon className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{title}</h3>
            <p className="text-muted-foreground max-w-md mb-6">
              {description}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-left">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialMediaTab;
